/**
 * The guided first match: a short checklist in a HUD panel (goal on top, one step at a time),
 * shown until the player has walked it once — every step done, a match played to its end, or
 * SKIP. Unlike the tips (`tips.ts`, 20 s each) a step stays until the player does the thing:
 *   push   — RIGHT-CLICK LAND NEXT TO YOURS TO PUSH YOUR BORDER
 *   silo   — 3 · BUILD A SILO (it launches your nukes)
 *   load   — SELECT THE SILO · F BUYS A MISSILE (waits while the silo is still being built)
 *   wait   — NUKES UNLOCK IN m:ss (done at weapons release; meanwhile: a city for income, and
 *            the SAMs every nation starts with will shoot down what comes in)
 *   launch — SELECT THE SILO · Q · CLICK A RIVAL CITY
 * A step is done once its thing exists (or happened) and stays done for the match, in any
 * order; the panel shows the first one not done yet. While the guide runs the build slots carry
 * their names and the slot a step asks for is lit (`slot`).
 */
import { BALANCE, TICK_HZ, type BuildKind, type PlayerView } from '@sa/sim';

export type GuideStep = 'push' | 'silo' | 'load' | 'wait' | 'launch';
export const GUIDE_STEPS: readonly GuideStep[] = ['push', 'silo', 'load', 'wait', 'launch'];

/** the closing line stays this long once the last step is done (ms) */
export const GUIDE_OUTRO_MS = 8000;

const KEY = 'sa.guide.v1';

export interface GuideLine {
  /** 1-based, of `GUIDE_STEPS.length` (0 for the closing line) */
  n: number;
  of: number;
  text: string;
  /** the build slot this step asks for (lit on the strip) */
  slot: BuildKind | null;
  /** the closing line: no step, no SKIP */
  outro: boolean;
}

type GuideStorage = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): GuideStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const clock = (secs: number) => {
  const s = Math.max(0, Math.ceil(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const GUIDE_GOAL = 'GOAL · TOP SCORE WHEN THE CLOCK HITS 0:00';

export class Guide {
  /** the player still has the guide to walk (this browser) */
  readonly active: boolean;
  private done = new Set<GuideStep>();
  private launchedOnce = false;
  private finishedMs: number | null = null;
  private over = false;

  constructor(private storage: GuideStorage | null = browserStorage()) {
    let seen = false;
    try {
      seen = storage?.getItem(KEY) === 'done';
    } catch {
      // unreadable: guide again
    }
    this.active = !seen;
  }

  /** On screen right now (active, not skipped, not past its closing line). */
  get showing(): boolean {
    return this.active && !this.over;
  }

  /** Our launch went through (the ICBM itself may be out of the view a moment later). */
  launched(): void {
    this.launchedOnce = true;
  }

  /** The steps done so far (in `GUIDE_STEPS` order). */
  get progress(): GuideStep[] {
    return GUIDE_STEPS.filter((s) => this.done.has(s));
  }

  /**
   * Mark what the view shows as done and return the line to show (null: nothing — not active,
   * eliminated, or finished). `onStep` hears each step as it is first done.
   */
  update(v: PlayerView, tick: number, nowMs: number, onStep?: (s: GuideStep) => void): GuideLine | null {
    if (!this.showing || v.eliminated) return null;
    let silo = false;
    let siloUp = false;
    let loaded = false;
    let icbm = false;
    for (const e of v.entities.values()) {
      if (e.owner !== v.me) continue;
      if (e.kind === 'silo') {
        silo = true;
        if (e.state !== 'building') siloUp = true;
        if ((e.detail?.loaded ?? 0) > 0 || (e.detail?.queued ?? 0) > 0) loaded = true;
      } else if (e.kind === 'icbm') icbm = true;
    }
    const mark = (s: GuideStep, now: boolean) => {
      if (!now || this.done.has(s)) return;
      this.done.add(s);
      onStep?.(s);
    };
    mark('push', v.focus !== null);
    mark('silo', silo);
    mark('load', loaded || icbm || this.launchedOnce);
    mark('wait', v.released);
    mark('launch', icbm || this.launchedOnce);
    const step = GUIDE_STEPS.find((s) => !this.done.has(s));
    if (!step) {
      this.finishedMs ??= nowMs;
      this.finish();
      if (nowMs - this.finishedMs >= GUIDE_OUTRO_MS) {
        this.over = true;
        return null;
      }
      return { n: 0, of: GUIDE_STEPS.length, text: 'THAT IS THE LOOP · PUSH · BUILD · STRIKE · DEFEND — ESC › RULES FOR THE REST', slot: null, outro: true };
    }
    return { n: GUIDE_STEPS.indexOf(step) + 1, of: GUIDE_STEPS.length, text: stepText(step, v, tick, silo, siloUp), slot: step === 'load' && !silo ? 'silo' : STEP_SLOT[step], outro: false };
  }

  /** SKIP: gone now and in later matches. */
  skip(): void {
    this.over = true;
    this.finish();
  }

  /** The match ended with the guide still up: one match played is the first match. */
  matchEnded(): void {
    if (this.active) this.finish();
  }

  private finish(): void {
    try {
      this.storage?.setItem(KEY, 'done');
    } catch {
      // storage blocked: the guide comes back next time
    }
  }
}

const STEP_SLOT: Record<GuideStep, BuildKind | null> = { push: null, silo: 'silo', load: null, wait: 'city', launch: null };

function stepText(s: GuideStep, v: PlayerView, tick: number, silo: boolean, siloUp: boolean): string {
  switch (s) {
    case 'push':
      return 'RIGHT-CLICK LAND NEXT TO YOURS TO PUSH YOUR BORDER';
    case 'silo':
      return '3 · BUILD A SILO · IT LAUNCHES YOUR NUKES';
    case 'load':
      if (!silo) return '3 · YOUR SILO IS GONE · BUILD ANOTHER';
      return siloUp ? 'CLICK YOUR SILO · F BUYS A MISSILE' : 'YOUR SILO IS BEING BUILT · KEEP PUSHING YOUR BORDER';
    case 'wait': {
      const left = (BALANCE.phases.releaseTick - tick) / TICK_HZ;
      return `NUKES UNLOCK IN ${clock(left)} · YOUR △ SAMS SHOOT DOWN INCOMING · MEANWHILE 4 · A CITY ADDS INCOME`;
    }
    case 'launch':
      return v.released ? 'CLICK YOUR SILO · Q · CLICK A RIVAL CITY TO LAUNCH' : 'NUKES UNLOCK SOON';
  }
}
